import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

const STORAGE_KEY = "weddingWizard:v1";
const TZ = "America/St_Johns";

const initialState = {
  // nav
  step: 0,

  // meta
  tz: TZ,
  source: "wizard",
  channel: "web",

  // planner-only hints (not sent to backend)
  planner: {
    guestsForSizing: 0,
  },

  // CUSTOMER (guest or logged-in snapshot)
  customer: {
    userId: null, // set server-side if logged-in
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    // NEW: backup contact
    phoneSecondary: "",
    phoneSecondaryLabel: "Backup",
    marketingOptIn: false,
  },

  // FULFILLMENT
  fulfillment: {
    method: "delivery", // 'delivery' | 'pickup'
    scheduledDate: "", // "YYYY-MM-DD" (server casts to Date)
    scheduledWindow: { start: "", end: "" }, // optional
    setupSelected: false,
    pickupLocation: "",
    waiver: {
      // acknowledgment for 3+ tier pickup risk
      mustAcknowledgeForPickup3Tiers: false,
      acknowledged: false,
      messageVersion: "v1",
      acknowledgedAt: null,
    },
    address: {
      line1: "",
      line2: "",
      city: "",
      province: "NL",
      postalCode: "",
      notes: "",
      geo: { lat: null, lng: null },
      distanceKm: 0,
    },
    notes: "",
  },

  // CAKE
  cake: {
    include: true,
    compositeKey: null, // e.g., '2T_TALL'
    styleId: null,
    styleName: "",
    tiers: [], // [{ position:'bottom'|'top'|..., flavorId, sizeInches? }]
    customizations: {}, // { color:'blue', textPiping:'Just Married', ... }
    notes: "",
  },

  // DESSERTS
  desserts: {
    include: false,
    selections: [], // [{ patisserieId, variantId?, quantity, notes? }]
  },

  // RENTALS
  rentals: {
    include: false,
    items: [], // [{ productId, variantId?, startDate, endDate, days, quantity, notes? }]
  },

  // PAYMENT (client-only convenience)
  payment: {
    mode: null, // 'element' | 'checkout' | null
    clientSecret: null,
    preference: {
      payNow: null,
      saveCardForAutoCharge: true,
    },
  },

  // server-set id
  weddingOrderId: null,
};

function reducer(state, action) {
  switch (action.type) {
    // nav
    case "SET_STEP":
      return { ...state, step: action.step };
    case "NEXT":
      return { ...state, step: Math.min(state.step + 1, 12) };
    case "BACK":
      return { ...state, step: Math.max(state.step - 1, 0) };

    // planner hints
    case "SET_GUESTS_FOR_SIZING":
      return {
        ...state,
        planner: {
          ...state.planner,
          guestsForSizing: Math.max(0, Number(action.value) || 0),
        },
      };

    // customer
    case "SET_CUSTOMER":
      return { ...state, customer: { ...state.customer, ...action.value } };

    // fulfillment
    case "SET_FULFILLMENT":
      return {
        ...state,
        fulfillment: { ...state.fulfillment, ...action.value },
      };
    case "SET_METHOD":
      return {
        ...state,
        fulfillment: { ...state.fulfillment, method: action.value },
      };
    case "SET_DATE":
      return {
        ...state,
        fulfillment: { ...state.fulfillment, scheduledDate: action.value },
      };
    case "SET_WINDOW":
      return {
        ...state,
        fulfillment: {
          ...state.fulfillment,
          scheduledWindow: {
            ...state.fulfillment.scheduledWindow,
            ...action.value,
          },
        },
      };
    case "SET_ADDRESS":
      return {
        ...state,
        fulfillment: {
          ...state.fulfillment,
          address: { ...state.fulfillment.address, ...action.value },
        },
      };
    case "SET_SETUP_SELECTED":
      return {
        ...state,
        fulfillment: { ...state.fulfillment, setupSelected: !!action.value },
      };
    case "SET_PICKUP_LOCATION":
      return {
        ...state,
        fulfillment: {
          ...state.fulfillment,
          pickupLocation: action.value || "",
        },
      };
    case "SET_WAIVER":
      return {
        ...state,
        fulfillment: {
          ...state.fulfillment,
          waiver: { ...state.fulfillment.waiver, ...action.value },
        },
      };

    // cake
    case "SET_CAKE_INCLUDE":
      return { ...state, cake: { ...state.cake, include: !!action.value } };
    case "SET_CAKE_STYLE": {
      const v = action.value;
      if (v && typeof v === "object") {
        // dispatch({ type:"SET_CAKE_STYLE", value: { id, name } })
        return {
          ...state,
          cake: {
            ...state.cake,
            styleId: v.id ?? state.cake.styleId ?? null,
            styleName: v.name ?? state.cake.styleName ?? "",
          },
        };
      }
      // legacy: dispatch({ type:"SET_CAKE_STYLE", value: id })
      return {
        ...state,
        cake: {
          ...state.cake,
          styleId: v,
          styleName: state.cake.styleName || "",
        },
      };
    }
    case "SET_CAKE_COMPOSITE":
      return { ...state, cake: { ...state.cake, compositeKey: action.value } };
    case "SET_CAKE_TIERS": {
      const next = Array.isArray(action.value) ? action.value : [];
      return {
        ...state,
        cake: {
          ...(state.cake || {}),
          tiers: next,
        },
      };
    }
    case "UPSERT_CAKE_TIER": {
      const { position, flavorId, flavorName, sizeInches } = action.value || {};
      if (!position) return state;

      const prev = Array.isArray(state.cake?.tiers) ? state.cake.tiers : [];
      const next = [...prev];
      const idx = next.findIndex((t) => t.position === position);

      const patch = {
        position,
        ...(typeof flavorId === "string" || typeof flavorId === "number"
          ? { flavorId: String(flavorId) }
          : {}),
        ...(typeof flavorName === "string" && flavorName.trim()
          ? { flavorNameSnapshot: flavorName.trim() }
          : {}),
        ...(Number.isFinite(sizeInches) ? { sizeInches } : {}),
      };

      if (idx >= 0) {
        next[idx] = { ...next[idx], ...patch };
      } else {
        next.push(patch);
      }

      return {
        ...state,
        cake: {
          ...(state.cake || {}),
          tiers: next,
        },
      };
    }

    case "SET_CAKE_CUSTOMIZATIONS":
      return {
        ...state,
        cake: {
          ...state.cake,
          customizations: { ...state.cake.customizations, ...action.value },
        },
      };
    case "SET_CAKE_NOTES":
      return { ...state, cake: { ...state.cake, notes: action.value || "" } };

    // desserts
    case "SET_DESSERTS_INCLUDE":
      return {
        ...state,
        desserts: { ...state.desserts, include: !!action.value },
      };
    case "SET_DESSERTS":
      const next = Array.isArray(action.value) ? action.value : [];
      return {
        ...state,
        desserts: {
          ...state.desserts,
          include: next.length > 0,
          selections: next,
        },
      };

    case "UPSERT_DESSERT": {
      const { patisserieId, variantId, quantity, ...rest } = action.value || {};
      if (!patisserieId) return state;
      const next = [...state.desserts.selections];
      const idx = next.findIndex(
        (d) => d.patisserieId === patisserieId && d.variantId === variantId
      );
      const qty = Math.max(0, Number(quantity) || 0);
      if (qty <= 0) {
        if (idx >= 0) next.splice(idx, 1);
      } else if (idx >= 0) {
        next[idx] = {
          ...next[idx],
          patisserieId,
          variantId,
          quantity: qty,
          ...rest,
        };
      } else {
        next.push({ patisserieId, variantId, quantity: qty, ...rest });
      }
      return {
        ...state,
        desserts: {
          ...state.desserts,
          include: next.length > 0,
          selections: next,
        },
      };
    }

    // rentals
    case "SET_RENTALS_INCLUDE":
      return {
        ...state,
        rentals: { ...state.rentals, include: !!action.value },
      };
    case "SET_RENTALS":
      return {
        ...state,
        rentals: { ...state.rentals, items: action.value || [] },
      };
    case "UPSERT_RENTAL": {
      const {
        productId,
        variantId,
        startDate,
        endDate,
        days,
        quantity = 1,
        ...rest
      } = action.value || {};
      if (!productId || !variantId) return state;
      const key = `${productId}:${variantId}`;
      const next = [...state.rentals.items];
      const i = next.findIndex((r) => `${r.productId}:${r.variantId}` === key);

      if (!startDate || !endDate || !days || quantity <= 0) {
        if (i >= 0) next.splice(i, 1);
      } else if (i >= 0) {
        next[i] = {
          ...next[i],
          productId,
          variantId,
          startDate,
          endDate,
          days,
          quantity,
          ...rest,
        };
      } else {
        next.push({
          productId,
          variantId,
          startDate,
          endDate,
          days,
          quantity,
          ...rest,
        });
      }
      return { ...state, rentals: { ...state.rentals, items: next } };
    }

    // payment & order
    case "SET_PAYMENT_INFO":
      return {
        ...state,
        payment: {
          mode: action.mode || null,
          clientSecret: action.clientSecret || null,
        },
      };
    case "SET_PAYMENT_PREFERENCE":
      return {
        ...state,
        payment: {
          ...state.payment,
          preference: { ...state.payment.preference, ...action.value },
        },
      };

    case "SET_WEDDING_ORDER_ID":
      return { ...state, weddingOrderId: action.value || null };

    // reset
    case "RESET":
      return { ...initialState };

    default:
      return state;
  }
}

const WizardContext = createContext(null);

export function useWeddingWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx)
    throw new Error(
      "useWeddingWizard must be used within WeddingWizardProvider"
    );
  return ctx;
}

export default function WeddingWizardProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load/persist — same v1 key, no migration
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      // restore step first
      dispatch({ type: "SET_STEP", step: parsed?.step ?? 0 });

      // reset to ensure clean base
      dispatch({ type: "RESET" });

      // restore safe keys
      if (parsed.customer)
        dispatch({ type: "SET_CUSTOMER", value: parsed.customer });

      if (parsed.fulfillment?.method)
        dispatch({ type: "SET_METHOD", value: parsed.fulfillment.method });
      if (parsed.fulfillment?.scheduledDate)
        dispatch({ type: "SET_DATE", value: parsed.fulfillment.scheduledDate });
      if (parsed.fulfillment?.scheduledWindow)
        dispatch({
          type: "SET_WINDOW",
          value: parsed.fulfillment.scheduledWindow,
        });
      if (parsed.fulfillment?.address)
        dispatch({ type: "SET_ADDRESS", value: parsed.fulfillment.address });
      if (typeof parsed.fulfillment?.setupSelected === "boolean")
        dispatch({
          type: "SET_SETUP_SELECTED",
          value: parsed.fulfillment.setupSelected,
        });
      if (parsed.fulfillment?.pickupLocation)
        dispatch({
          type: "SET_PICKUP_LOCATION",
          value: parsed.fulfillment.pickupLocation,
        });
      if (parsed.fulfillment?.waiver)
        dispatch({ type: "SET_WAIVER", value: parsed.fulfillment.waiver });

      if (parsed.planner?.guestsForSizing != null)
        dispatch({
          type: "SET_GUESTS_FOR_SIZING",
          value: parsed.planner.guestsForSizing,
        });

      if (parsed.cake) {
        dispatch({ type: "SET_CAKE_INCLUDE", value: !!parsed.cake.include });
        dispatch({
          type: "SET_CAKE_COMPOSITE",
          value: parsed.cake.compositeKey || null,
        });
        if (parsed.cake.styleId || parsed.cake.styleName) {
          dispatch({
            type: "SET_CAKE_STYLE",
            value: {
              id: parsed.cake.styleId || null,
              name: parsed.cake.styleName || "",
            },
          });
        }
        dispatch({ type: "SET_CAKE_TIERS", value: parsed.cake.tiers || [] });
        if (parsed.cake.customizations) {
          dispatch({
            type: "SET_CAKE_CUSTOMIZATIONS",
            value: parsed.cake.customizations,
          });
        }
      }

      if (parsed.desserts) {
        dispatch({
          type: "SET_DESSERTS_INCLUDE",
          value: !!parsed.desserts.include,
        });
        dispatch({
          type: "SET_DESSERTS",
          value: parsed.desserts.selections || [],
        });
      }

      if (parsed.rentals) {
        dispatch({
          type: "SET_RENTALS_INCLUDE",
          value: !!parsed.rentals.include,
        });
        dispatch({ type: "SET_RENTALS", value: parsed.rentals.items || [] });
      }

      if (parsed.payment) {
        dispatch({
          type: "SET_PAYMENT_INFO",
          mode: parsed.payment.mode,
          clientSecret: parsed.payment.clientSecret,
        });
      }

      if (parsed.weddingOrderId)
        dispatch({
          type: "SET_WEDDING_ORDER_ID",
          value: parsed.weddingOrderId,
        });
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  const api = useMemo(() => {
    const buildOrderPayload = () => ({
      source: state.source,
      channel: state.channel,
      tz: state.tz,

      customer: {
        firstName: state.customer.firstName?.trim(),
        lastName: state.customer.lastName?.trim(),
        email: state.customer.email?.trim().toLowerCase(),
        phone: state.customer.phone?.trim() || undefined,
        // NEW: backup contact
        phoneSecondary: state.customer.phoneSecondary?.trim() || undefined,
        phoneSecondaryLabel:
          state.customer.phoneSecondaryLabel?.trim() || undefined,
        marketingOptIn: !!state.customer.marketingOptIn,
      },

      fulfillment: state.fulfillment,

      cake: state.cake.include
        ? {
            include: true,
            compositeKey: state.cake.compositeKey,
            styleId: state.cake.styleId,
            tiers: state.cake.tiers,
            customizations: state.cake.customizations,
            notes: state.cake.notes,
          }
        : { include: false },

      desserts: {
        include: state.desserts.include,
        selections: state.desserts.selections,
      },

      rentals: {
        include: state.rentals.include,
        items: state.rentals.items,
      },

      createdFromVersion: 1, // staying on v1
    });

    return {
      state,
      // nav
      setStep: (step) => dispatch({ type: "SET_STEP", step }),
      next: () => dispatch({ type: "NEXT" }),
      back: () => dispatch({ type: "BACK" }),

      // planner
      setGuestsForSizing: (value) =>
        dispatch({ type: "SET_GUESTS_FOR_SIZING", value }),

      // customer
      setCustomer: (value) => dispatch({ type: "SET_CUSTOMER", value }),

      // fulfillment
      setFulfillment: (value) => dispatch({ type: "SET_FULFILLMENT", value }),
      setMethod: (value) => dispatch({ type: "SET_METHOD", value }),
      setDate: (iso) => dispatch({ type: "SET_DATE", value: iso }),
      setWindow: (w) => dispatch({ type: "SET_WINDOW", value: w }),
      setAddress: (addr) => dispatch({ type: "SET_ADDRESS", value: addr }),
      setSetupSelected: (v) =>
        dispatch({ type: "SET_SETUP_SELECTED", value: v }),
      setPickupLocation: (loc) =>
        dispatch({ type: "SET_PICKUP_LOCATION", value: loc }),
      setWaiver: (kv) => dispatch({ type: "SET_WAIVER", value: kv }),

      // cake
      setCakeInclude: (v) => dispatch({ type: "SET_CAKE_INCLUDE", value: v }),
      setCakeStyleId: (id, name) => {
        // backward compatible: if only id passed, still works
        if (id && typeof id === "object") {
          dispatch({ type: "SET_CAKE_STYLE", value: id }); // { id, name }
        } else {
          dispatch({ type: "SET_CAKE_STYLE", value: name ? { id, name } : id });
        }
      },
      setCompositeKey: (k) =>
        dispatch({ type: "SET_CAKE_COMPOSITE", value: k }),
      setCakeTiers: (arr) => dispatch({ type: "SET_CAKE_TIERS", value: arr }),
      upsertCakeTier: (tier) =>
        dispatch({ type: "UPSERT_CAKE_TIER", value: tier }),
      setCustomizations: (kv) =>
        dispatch({ type: "SET_CAKE_CUSTOMIZATIONS", value: kv }),
      setCakeNotes: (txt) => dispatch({ type: "SET_CAKE_NOTES", value: txt }),

      // desserts
      setDessertsInclude: (v) =>
        dispatch({ type: "SET_DESSERTS_INCLUDE", value: v }),
      setDesserts: (arr) => dispatch({ type: "SET_DESSERTS", value: arr }),
      upsertDessert: (payload) =>
        dispatch({ type: "UPSERT_DESSERT", value: payload }),

      // rentals
      setRentalsInclude: (v) =>
        dispatch({ type: "SET_RENTALS_INCLUDE", value: v }),
      setRentals: (arr) => dispatch({ type: "SET_RENTALS", value: arr }),
      upsertRental: (payload) =>
        dispatch({ type: "UPSERT_RENTAL", value: payload }),

      // payment + order
      setPaymentInfo: (mode, clientSecret) =>
        dispatch({ type: "SET_PAYMENT_INFO", mode, clientSecret }),
      setPaymentPreference: (pref) =>
        dispatch({ type: "SET_PAYMENT_PREFERENCE", value: pref }),
      setWeddingOrderId: (id) =>
        dispatch({ type: "SET_WEDDING_ORDER_ID", value: id }),

      // server payload
      buildOrderPayload,

      // reset
      reset: () => dispatch({ type: "RESET" }),
    };
  }, [state]);

  return (
    <WizardContext.Provider value={api}>{children}</WizardContext.Provider>
  );
}
